import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Button,
  Paper,
  IconButton,
  Chip,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  Pause as PauseIcon,
  PlayArrow as ResumeIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { apiService } from '../services/api';

interface LargeFileUploaderProps {
  channelId: string;
  onUploadComplete: (message: any) => void;
  onClose: () => void;
}

interface UploadState {
  status: 'idle' | 'initializing' | 'uploading' | 'paused' | 'completing' | 'completed' | 'error';
  file: File | null;
  uploadId: string | null;
  key: string | null;
  totalParts: number;
  uploadedParts: number;
  currentPartProgress: number;
  completedParts: Array<{ partNumber: number; etag: string }>;
  partUrls: Array<{ partNumber: number; signedUrl: string }>;
  chunkSize: number;
  retentionDays: number | null;
  expiresAt: string | null;
  error: string | null;
}

// File size formatting
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Retention info based on file size
const getRetentionInfo = (fileSize: number): { days: number | null; label: string; color: 'success' | 'warning' | 'error'; rejected?: boolean } => {
  const TEN_MB = 10 * 1024 * 1024;
  const HUNDRED_MB = 100 * 1024 * 1024;
  const ONE_GB = 1024 * 1024 * 1024;

  if (fileSize > ONE_GB) {
    return { days: null, label: 'File is too large (max 1GB)', color: 'error', rejected: true };
  } else if (fileSize <= TEN_MB) {
    return { days: null, label: 'Stored forever', color: 'success' };
  } else if (fileSize <= HUNDRED_MB) {
    return { days: 90, label: 'Stored for 90 days', color: 'warning' };
  } else {
    return { days: 7, label: 'Stored for 7 days', color: 'error' };
  }
};

export const LargeFileUploader: React.FC<LargeFileUploaderProps> = ({
  channelId,
  onUploadComplete,
  onClose,
}) => {
  const [state, setState] = useState<UploadState>({
    status: 'idle',
    file: null,
    uploadId: null,
    key: null,
    totalParts: 0,
    uploadedParts: 0,
    currentPartProgress: 0,
    completedParts: [],
    partUrls: [],
    chunkSize: 100 * 1024 * 1024, // 100MB default
    retentionDays: null,
    expiresAt: null,
    error: null,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const isPausedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setState(prev => ({
        ...prev,
        file,
        status: 'idle',
        error: null,
        uploadedParts: 0,
        completedParts: [],
      }));
    }
  };

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file) {
      setState(prev => ({
        ...prev,
        file,
        status: 'idle',
        error: null,
        uploadedParts: 0,
        completedParts: [],
      }));
    }
  }, []);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
  }, []);

  const startUpload = async () => {
    if (!state.file) return;

    isPausedRef.current = false;
    
    try {
      setState(prev => ({ ...prev, status: 'initializing', error: null }));

      // 1. Initiate multipart upload
      const initResponse = await apiService.initiateMultipartUpload({
        fileName: state.file.name,
        fileSize: state.file.size,
        contentType: state.file.type || 'application/octet-stream',
        channelId,
      });

      console.log('📤 Upload initiated:', initResponse);

      setState(prev => ({
        ...prev,
        uploadId: initResponse.uploadId,
        key: initResponse.key,
        totalParts: initResponse.totalParts,
        partUrls: initResponse.partUrls,
        chunkSize: initResponse.chunkSize,
        retentionDays: initResponse.retentionDays,
        expiresAt: initResponse.expiresAt,
        status: 'uploading',
      }));

      // 2. Upload each part
      await uploadParts(
        state.file,
        initResponse.uploadId,
        initResponse.key,
        initResponse.totalParts,
        initResponse.chunkSize,
        initResponse.partUrls
      );

    } catch (error: any) {
      console.error('Upload error:', error);
      setState(prev => ({
        ...prev,
        status: 'error',
        error: error.message || 'Upload failed',
      }));
    }
  };

  const uploadParts = async (
    file: File,
    uploadId: string,
    key: string,
    totalParts: number,
    chunkSize: number,
    initialPartUrls: Array<{ partNumber: number; signedUrl: string }>
  ) => {
    const completedParts: Array<{ partNumber: number; etag: string }> = [...state.completedParts];
    let partUrls = [...initialPartUrls];
    let currentUrlIndex = 0;

    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      // Check if paused
      if (isPausedRef.current) {
        setState(prev => ({ ...prev, status: 'paused' }));
        return;
      }

      // Skip already uploaded parts (for resume)
      if (completedParts.some(p => p.partNumber === partNumber)) {
        continue;
      }

      // Get signed URL for this part
      let partUrl = partUrls.find(p => p.partNumber === partNumber);
      
      // If we don't have a URL for this part, fetch more
      if (!partUrl) {
        try {
          const moreUrls = await apiService.getMorePartUrls({
            uploadId,
            key,
            startPart: partNumber,
            endPart: Math.min(partNumber + 99, totalParts),
          });
          partUrls = [...partUrls, ...moreUrls.partUrls];
          partUrl = partUrls.find(p => p.partNumber === partNumber);
        } catch (error) {
          console.error('Failed to get more URLs:', error);
          throw new Error('Failed to get upload URLs');
        }
      }

      if (!partUrl) {
        throw new Error(`No signed URL for part ${partNumber}`);
      }

      // Calculate chunk boundaries
      const start = (partNumber - 1) * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      console.log(`📤 Uploading part ${partNumber}/${totalParts} (${formatFileSize(chunk.size)})`);

      try {
        // Upload chunk directly to R2
        const etag = await apiService.uploadChunkToR2(
          partUrl.signedUrl,
          chunk,
          (loaded, total) => {
            const partProgress = (loaded / total) * 100;
            setState(prev => ({ ...prev, currentPartProgress: partProgress }));
          }
        );

        completedParts.push({ partNumber, etag });
        
        setState(prev => ({
          ...prev,
          uploadedParts: completedParts.length,
          completedParts: [...completedParts],
          currentPartProgress: 0,
        }));

        console.log(`✅ Part ${partNumber} uploaded, ETag: ${etag}`);

      } catch (error: any) {
        console.error(`❌ Part ${partNumber} failed:`, error);
        throw new Error(`Failed to upload part ${partNumber}: ${error.message}`);
      }
    }

    // 3. Complete the multipart upload
    setState(prev => ({ ...prev, status: 'completing' }));

    try {
      const completeResponse = await apiService.completeMultipartUpload({
        uploadId,
        key,
        parts: completedParts,
        channelId,
      });

      console.log('✅ Upload completed:', completeResponse);

      setState(prev => ({ ...prev, status: 'completed' }));
      
      // Notify parent with the new message
      onUploadComplete(completeResponse.message);

    } catch (error: any) {
      console.error('❌ Complete failed:', error);
      throw new Error(`Failed to complete upload: ${error.message}`);
    }
  };

  const pauseUpload = () => {
    isPausedRef.current = true;
    setState(prev => ({ ...prev, status: 'paused' }));
  };

  const resumeUpload = async () => {
    if (!state.file || !state.uploadId || !state.key) return;

    isPausedRef.current = false;
    setState(prev => ({ ...prev, status: 'uploading' }));

    try {
      // Get fresh URLs for remaining parts
      const remainingStart = state.uploadedParts + 1;
      const moreUrls = await apiService.getMorePartUrls({
        uploadId: state.uploadId,
        key: state.key,
        startPart: remainingStart,
        endPart: Math.min(remainingStart + 99, state.totalParts),
      });

      await uploadParts(
        state.file,
        state.uploadId,
        state.key,
        state.totalParts,
        state.chunkSize,
        moreUrls.partUrls
      );
    } catch (error: any) {
      setState(prev => ({
        ...prev,
        status: 'error',
        error: error.message,
      }));
    }
  };

  const cancelUpload = async () => {
    if (state.uploadId && state.key) {
      try {
        await apiService.abortMultipartUpload({
          uploadId: state.uploadId,
          key: state.key,
        });
      } catch (error) {
        console.error('Failed to abort upload:', error);
      }
    }
    onClose();
  };

  const totalProgress = state.totalParts > 0 
    ? ((state.uploadedParts + state.currentPartProgress / 100) / state.totalParts) * 100 
    : 0;

  const retentionInfo = state.file ? getRetentionInfo(state.file.size) : null;

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 2,
        maxWidth: 500,
        mx: 'auto',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          📁 Upload large file
        </Typography>
        <IconButton size="small" onClick={cancelUpload}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* File selection */}
      {state.status === 'idle' && !state.file && (
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: '2px dashed #ccc',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: '#1976d2',
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <UploadIcon sx={{ fontSize: 48, color: '#666', mb: 2 }} />
          <Typography variant="body1" color="textSecondary">
            Drag and drop a file here, or click to select
          </Typography>
          <Typography variant="caption" color="textSecondary" display="block" mt={1}>
            Max 1GB • All file types allowed
          </Typography>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </Box>
      )}

      {/* Selected file info */}
      {state.file && (
        <Box mb={2}>
          <Box display="flex" alignItems="center" gap={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
            <FileIcon sx={{ fontSize: 40, color: '#666' }} />
            <Box flex={1}>
              <Typography variant="body1" fontWeight={500} noWrap>
                {state.file.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {formatFileSize(state.file.size)}
              </Typography>
            </Box>
            {state.status === 'idle' && (
              <IconButton size="small" onClick={() => setState(prev => ({ ...prev, file: null }))}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {/* Retention warning */}
          {retentionInfo && (
            <Alert 
              severity={retentionInfo.color === 'success' ? 'success' : retentionInfo.color === 'warning' ? 'warning' : 'error'}
              sx={{ mt: 1 }}
              icon={retentionInfo.rejected ? <WarningIcon /> : retentionInfo.days ? <WarningIcon /> : <CheckIcon />}
            >
              <Typography variant="body2">
                {retentionInfo.label}
                {retentionInfo.days && !retentionInfo.rejected && (
                  <Typography variant="caption" display="block" color="textSecondary">
                    Files over {retentionInfo.days === 7 ? '100MB' : '10MB'} are automatically deleted after {retentionInfo.days} days
                  </Typography>
                )}
              </Typography>
            </Alert>
          )}
        </Box>
      )}

      {/* Progress */}
      {['initializing', 'uploading', 'paused', 'completing'].includes(state.status) && (
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="body2" color="textSecondary">
              {state.status === 'initializing' && 'Initializing upload...'}
              {state.status === 'uploading' && `Uploading part ${state.uploadedParts + 1} of ${state.totalParts}`}
              {state.status === 'paused' && 'Paused'}
              {state.status === 'completing' && 'Completing upload...'}
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {Math.round(totalProgress)}%
            </Typography>
          </Box>
          <LinearProgress
            variant={state.status === 'initializing' || state.status === 'completing' ? 'indeterminate' : 'determinate'}
            value={totalProgress}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Box display="flex" justifyContent="space-between" mt={0.5}>
            <Typography variant="caption" color="textSecondary">
              {state.uploadedParts} / {state.totalParts} parts completed
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatFileSize(state.uploadedParts * state.chunkSize)} / {state.file ? formatFileSize(state.file.size) : ''}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Completed */}
      {state.status === 'completed' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            ✅ File uploaded!
          </Typography>
        </Alert>
      )}

      {/* Error */}
      {state.status === 'error' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2">
            ❌ {state.error}
          </Typography>
        </Alert>
      )}

      {/* Actions */}
      <Box display="flex" gap={1} justifyContent="flex-end">
        {state.status === 'idle' && state.file && !retentionInfo?.rejected && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<UploadIcon />}
            onClick={startUpload}
          >
            Start upload
          </Button>
        )}

        {state.status === 'uploading' && (
          <Button
            variant="outlined"
            startIcon={<PauseIcon />}
            onClick={pauseUpload}
          >
            Pause
          </Button>
        )}

        {state.status === 'paused' && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<ResumeIcon />}
            onClick={resumeUpload}
          >
            Resume
          </Button>
        )}

        {state.status === 'error' && (
          <Button
            variant="contained"
            color="primary"
            onClick={startUpload}
          >
            Try again
          </Button>
        )}

        {state.status === 'completed' && (
          <Button
            variant="contained"
            color="primary"
            onClick={onClose}
          >
            Close
          </Button>
        )}

        {['idle', 'error', 'paused'].includes(state.status) && (
          <Button variant="outlined" onClick={cancelUpload}>
            Cancel
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default LargeFileUploader;
